import Keycloak, { KeycloakInitOptions } from "keycloak-js";
import * as Sentry from "@sentry/react";
import store from "../store";
import { updateActiveClientId } from "@features/clientDataSlice";
import {
    clearAllCookies,
    getClientId,
    logOut,
    showSentryCrashReportModal,
} from "@ts/utils";
import axiosInstance from "@/ts/axiosInstance";
import { resetHeapIdentity } from "./HeapService";
import { IAccess, IClientAccess } from "@/ts/common-types";
import { renderServerError } from "../main";

type TInitOptions = {
    url: string;
    realm: string;
    clientId: string;
    onLoad: string;
};

declare global {
    interface Storage {
        setItem(key: string, value: any): void;
    }
}

const initOptions: TInitOptions = {
    url: process.env.VITE_OAUTH_URL,
    realm: process.env.VITE_OAUTH_REALM,
    clientId: process.env.VITE_OAUTH_CLIENT_ID,
    onLoad: process.env.VITE_OAUTH_ON_LOAD,
};

export const _kc = new Keycloak(initOptions);

export const initKeycloak = async (onAuthenticatedCallback: {
    (): Promise<void>;
    (): void;
}) => {
    try {
        Sentry.addBreadcrumb({
            category: "authentication",
            message: "Getting auth tokens",
            level: "info",
        });
        const curTokenResponse = await getAuthTokens();

        Sentry.addBreadcrumb({
            category: "authentication",
            message: "Initializing Keycloak",
            level: "info",
            data: {
                new_session: curTokenResponse.new_session,
            },
        });

        const authenticated = await _kc.init({
            onLoad: initOptions.onLoad,
            checkLoginIframe: false,
            ...(!curTokenResponse.new_session
                ? {
                      token: curTokenResponse.access_token,
                      refreshToken: curTokenResponse.refresh_token,
                  }
                : {}),
        } as KeycloakInitOptions);

        Sentry.addBreadcrumb({
            category: "authentication",
            message: "Keycloak initialization complete",
            level: "info",
            data: {
                authenticated,
            },
        });

        if (!authenticated) {
            Sentry.addBreadcrumb({
                category: "authentication",
                message: "Not authenticated, reloading page",
                level: "warning",
            });
            window.location.reload();
        }

        Sentry.addBreadcrumb({
            category: "authentication",
            message: "Setting auth tokens",
            level: "info",
        });

        const tokenResponse = await setAuthTokens();
        if (!tokenResponse.status) {
            throw new Error("Failed to fetch token");
        }

        Sentry.addBreadcrumb({
            category: "authentication",
            message: "Authentication complete",
            level: "info",
        });
        //ToDo: Need to check why we need to update activeClientId here
        store.dispatch(updateActiveClientId(getClientId()));
        const { name, email, cube_role } = _kc.tokenParsed;
        sessionStorage.setItem(
            "chatbot",
            JSON.stringify({
                username: name,
                email,
            })
        );

        if (cube_role) {
            // Abhishek: 05 Dec 2025
            // TODO: Remove this after we have a proper way to store cube_role
            sessionStorage.setItem("cube_role", cube_role);
        }
        onAuthenticatedCallback();
    } catch (err: any) {
        console.error(err);
        showSentryCrashReportModal(true, err);
        // Show 500 error page on authentication failure
        renderServerError();
    }
};

export const doLogin = _kc.login;

export const doLogout = async () => {
    // First clear all cookies to prevent any new refresh token from being set
    clearAllCookies();

    // Then call the logout endpoint
    await logOut();

    // Reset Heap identity before clearing storage
    resetHeapIdentity();

    // Finally perform other cleanup operations
    sessionStorage.clear();
    localStorage.clear();
    Sentry.setUser(null);
    await _kc.logout();

    return true;
};

export const setAuthTokens = async () => {
    try {
        const response = await axiosInstance({
            url: "/auth",
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            data: {
                accessToken: _kc.token,
                refreshToken: _kc.refreshToken,
            },
        });
        return response;
    } catch (err: any) {
        console.error("Set Auth Tokens Error:", err);

        // Capture error in Sentry with context
        Sentry.captureException(err, {
            level: "error",
            tags: {
                error_type: "auth_token_set_error",
                location: "UserService.ts",
                function: "setAuthTokens",
            },
            contexts: {
                token_set: {
                    timestamp: new Date().toISOString(),
                    endpoint: "/auth",
                    method: "POST",
                },
            },
            extra: {
                errorMessage: err?.message || String(err),
                responseStatus: err?.response?.status,
                responseData: err?.response?.data,
                hasAccessToken: !!_kc.token,
                hasRefreshToken: !!_kc.refreshToken,
            },
        });

        throw new Error("Failed to fetch token");
    }
};

export const getAuthTokens = async () => {
    try {
        const response = await axiosInstance("/auth");
        if (!response.status) {
            throw new Error("Failed to fetch token");
        }
        return response.data;
    } catch (err: any) {
        console.error("Get Auth Tokens Error:", err);

        // Capture error in Sentry with context
        Sentry.captureException(err, {
            level: "error",
            tags: {
                error_type: "auth_token_fetch_error",
                location: "UserService.ts",
                function: "getAuthTokens",
            },
            contexts: {
                token_fetch: {
                    timestamp: new Date().toISOString(),
                    endpoint: "/auth",
                },
            },
            extra: {
                errorMessage: err?.message || String(err),
                responseStatus: err?.response?.status,
                responseData: err?.response?.data,
            },
        });

        // Return a default object indicating new session if auth tokens can't be retrieved
        return { new_session: true };
    }
};

export const getProfiles = async () => {
    const response = await axiosInstance({
        url: "/user/clients",
        method: "GET",
        headers: {
            "Content-Type": "application/json; charset=utf-8",
        },
    });

    return response.data;
};

export const isLoggedIn = () => !!_kc.token;

export const getUsername = () => _kc.tokenParsed?.name;

export const getEmail = () => _kc.tokenParsed?.email;

export const getUserRole = () => {
    const state: any = store.getState();
    const userData = state.userData?.value;
    const userAccess = userData?.access?.find((access: IAccess) =>
        access.clients.find(
            (client: IClientAccess) => client.client_id === getClientId()
        )
    );
    return userAccess?.role_slug ?? "user";
};
